import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiTrash2, FiSearch, FiUploadCloud, FiX, FiUser, FiEye } from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { useStaffLog } from '../utils/useStaffLog';
import './AdminPurchaseForm.css';

/* ── Shared inline styles (non-layout, kept as-is) ─────────────── */
const label   = { display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 6 };
const input   = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: '0.88rem', outline: 'none', fontFamily: 'inherit', background: '#fff' };
const select  = { ...input, background: '#fff', cursor: 'pointer' };
const ro      = { ...input, background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' };
const money   = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Round-off warning threshold (₹) — soft warning only, never blocks saving
const ROUND_OFF_WARN = 10;

// Pure totals calculation. manualRoundOff = null -> automatic round-off,
// otherwise the (string/number) value typed by the user is used as-is.
const computePurchaseTotals = (items, otherCharges, taxType, manualRoundOff = null) => {
  let subtotal = 0, discountTotal = 0, taxableTotal = 0;
  let cgstTotal = 0, sgstTotal = 0, igstTotal = 0;
  (items || []).forEach(item => {
    const qty  = Number(item.quantity)        || 0;
    const rate = Number(item.rate)            || 0;
    const disc = Number(item.discount_amount) || 0;
    const tr   = Number(item.tax_rate)        || 0;
    const lineSub  = qty * rate;
    const taxable  = Math.max(0, lineSub - disc);
    const half     = tr / 2;
    subtotal      += lineSub;
    discountTotal += disc;
    taxableTotal  += taxable;
    if (taxType === 'INTRA_STATE') {
      cgstTotal += Number((taxable * half / 100).toFixed(2));
      sgstTotal += Number((taxable * half / 100).toFixed(2));
    } else {
      igstTotal += Number((taxable * tr / 100).toFixed(2));
    }
  });
  const totalGST    = cgstTotal + sgstTotal + igstTotal;
  // toFixed(2) first so float noise (e.g. 1.005) can't push us a paisa off
  const beforeRound = Number((taxableTotal + totalGST + Number(otherCharges || 0)).toFixed(2));
  const autoRoundOff = Number((Math.round(beforeRound) - beforeRound).toFixed(2));
  const isManual    = manualRoundOff !== null && manualRoundOff !== undefined;
  const roundOff    = isManual ? (Number(manualRoundOff) || 0) : autoRoundOff;
  const grandTotal  = Number((beforeRound + roundOff).toFixed(2));
  return { subtotal, discountTotal, taxableTotal, cgstTotal, sgstTotal, igstTotal, totalGST, autoRoundOff, roundOff, isManual, grandTotal };
};

const UNITS = ['Nos', 'Box', 'Kg', 'Gram', 'Sheet', 'Roll', 'Meter', 'Book', 'Set', 'Lot', 'Pc', 'Sqft', 'Ltr'];

/* ── PDF Invoice Parser ─────────────────────────────────────────── */
const parsePdfInvoice = async (file) => {
  try {
    if (!window.pdfjsLib) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
    }

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let fullText = '';
    for (let i = 1; i <= Math.min(pdf.numPages, 3); i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      fullText += content.items.map(s => s.str).join(' ') + '\n';
    }

    const gstinMatch = fullText.match(/\b(\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1})\b/);
    const gstin = gstinMatch ? gstinMatch[1] : null;

    const invMatch = fullText.match(/invoice\s*no\.?\s*[:\-]?\s*([A-Z0-9][A-Z0-9\/\-]{2,30})/i);
    const invoiceNumber = invMatch ? invMatch[1].trim() : null;

    const dateMatch = fullText.match(
      /(?:invoice\s*date|bill\s*date|dated?)\s*[:\-]?\s*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}|\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{2,4})/i
    );
    let invoiceDate = null;
    if (dateMatch) {
      const raw = dateMatch[1].trim();
      const parts = raw.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
      if (parts) {
        const [, d, m, y] = parts;
        const year = y.length === 2 ? `20${y}` : y;
        invoiceDate = `${year}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      } else {
        const parsed = new Date(raw);
        if (!isNaN(parsed)) invoiceDate = parsed.toISOString().split('T')[0];
      }
    }

    return { gstin, invoiceNumber, invoiceDate };
  } catch (e) {
    console.error('PDF parse error:', e);
    return { gstin: null, invoiceNumber: null, invoiceDate: null };
  }
};

const blankItem = () => ({
  _key: Date.now() + Math.random(),
  item_name: '', description: '', quantity: '', unit: 'Nos',
  rate: '', discount_amount: 0, tax_rate: 18,
});

/* ── Order search dropdown ──────────────────────────────────────── */
function OrderSearch({ value, onChange }) {
  const [q, setQ]         = useState('');
  const [results, setRes] = useState([]);
  const [open, setOpen]   = useState(false);
  const ref               = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (term) => {
    if (!term.trim()) { setRes([]); return; }
    try {
      const { data } = await API.get('/sales-orders', { params: { q: term, limit: 10 } });
      setRes(data.orders || []);
    } catch {}
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(q), 300);
    return () => clearTimeout(t);
  }, [q, search]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <FiSearch size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
        <input
          style={{ ...input, paddingLeft: 28 }}
          placeholder="Search order no. or customer…"
          value={value ? `${value.orderNumber} — ${value.customerName}` : q}
          onChange={e => { setQ(e.target.value); onChange(null); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        {value && (
          <button
            onClick={() => { onChange(null); setQ(''); }}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, lineHeight: 1 }}
          >×</button>
        )}
      </div>
      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 200, maxHeight: 220, overflowY: 'auto' }}>
          {results.map(o => (
            <div
              key={o.id}
              onClick={() => { onChange(o); setOpen(false); setQ(''); }}
              style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#6366f1', fontWeight: 600 }}>{o.orderNumber}</span>
              <span style={{ marginLeft: 10, fontSize: 13, color: '#374151' }}>{o.customerName}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Vendor search + info card ──────────────────────────────────── */
function VendorBlock({ vendor, setVendor }) {
  const [query,     setQuery]     = useState('');
  const [matches,   setMatches]   = useState([]);
  const [searching, setSearching] = useState(false);
  const [open,      setOpen]      = useState(false);
  const debounceRef = useRef(null);
  const wrapRef     = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 1) { setMatches([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        setSearching(true);
        const { data } = await API.get('/vendors', { params: { q: query.trim(), limit: 10, status: 'active' } });
        setMatches(data.vendors || []);
      } catch { /* silent */ }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const applyMatch = (v) => {
    const address = [v.address_line1, v.address_line2, v.city, v.state, v.pincode]
      .filter(Boolean).join(', ');
    setVendor({
      id:             v.id,
      vendor_code:    v.vendor_code,
      company_name:   v.company_name,
      contact_person: v.contact_person || '',
      phone:          v.phone || '',
      email:          v.email || '',
      gstin:          v.gstin || '',
      address:        address,
      payment_terms_days: v.payment_terms_days || 0,
    });
    setOpen(false);
    setQuery('');
  };

  const clearVendor = () => { setVendor(null); setQuery(''); };

  if (vendor) {
    return (
      <div className="apf-vendor-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: '0.76rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Vendor</span>
          <button
            onClick={clearVendor}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--primary, #6366f1)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
          >
            <FiSearch size={11} /> Change
          </button>
        </div>
        <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text, #111827)', marginBottom: 2 }}>{vendor.company_name}</div>
        {vendor.contact_person && (
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted, #6b7280)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
            <FiUser size={11} /> {vendor.contact_person}
          </div>
        )}
        {vendor.phone && <div style={{ fontSize: '0.85rem', color: 'var(--text-muted, #6b7280)', marginTop: 2 }}>{vendor.phone}{vendor.email ? ` · ${vendor.email}` : ''}</div>}
        {vendor.gstin && <div style={{ fontSize: '0.85rem', color: 'var(--text-muted, #6b7280)', marginTop: 2 }}>GSTIN: {vendor.gstin}</div>}
        {vendor.address && <div style={{ fontSize: '0.85rem', color: 'var(--text-muted, #6b7280)', marginTop: 2, lineHeight: 1.5 }}>{vendor.address}</div>}
      </div>
    );
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <label style={label}>Vendor *</label>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e5e7eb', borderRadius: 9, padding: '9px 12px', background: '#f9fafb' }}>
        <FiSearch size={15} style={{ color: '#9ca3af', flexShrink: 0 }} />
        <input
          style={{ border: 'none', background: 'transparent', outline: 'none', flex: 1, fontSize: '0.9rem', fontFamily: 'inherit' }}
          placeholder="Search vendor by name, code or phone…"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
        />
        {searching && (
          <span style={{ width: 14, height: 14, border: '2px solid #e5e7eb', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'pu-spin 0.7s linear infinite', flexShrink: 0 }} />
        )}
      </div>
      {open && (matches.length > 0 || (query.trim().length >= 1 && !searching)) && (
        <div style={{ position: 'absolute', zIndex: 200, top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', maxHeight: 280, overflowY: 'auto', padding: 6 }}>
          {matches.length === 0 ? (
            <div style={{ padding: '12px 10px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>No vendors found</div>
          ) : matches.map(v => (
            <button
              key={v.id}
              onClick={() => applyMatch(v)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 10px', borderRadius: 8, border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              <span style={{ background: '#eef2ff', color: '#6366f1', borderRadius: 6, padding: '2px 7px', fontSize: 11, fontWeight: 700, fontFamily: 'monospace', flexShrink: 0 }}>{v.vendor_code}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: '#111827', display: 'block' }}>{v.company_name}</span>
                {v.contact_person && <span style={{ fontSize: 12, color: '#6b7280' }}>{v.contact_person}{v.phone ? ` · ${v.phone}` : ''}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
      <style>{`@keyframes pu-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ── Invoice preview modal ──────────────────────────────────────── */
function InvoicePreviewModal({ doc, onClose }) {
  const isPdf = doc.document_name.toLowerCase().endsWith('.pdf');

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12,
          width: '95vw', maxWidth: 900,
          height: '90vh',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>
            {doc.document_name}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <a
              href={doc.file_path}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: '#6366f1', fontWeight: 600, textDecoration: 'none' }}
            >
              Open ↗
            </a>
            <button
              onClick={onClose}
              style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <FiX size={15} color="#374151" />
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'hidden', background: '#f9fafb' }}>
          {isPdf ? (
            <iframe
              src={doc.file_path}
              title={doc.document_name}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
              <img
                src={doc.file_path}
                alt={doc.document_name}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6, boxShadow: '0 2px 12px rgba(0,0,0,0.12)' }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Drag-and-drop vendor invoice upload ────────────────────────── */
function DragDropUpload({ documents, onUpload, onRemove, uploading }) {
  const [dragging,   setDragging]   = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileRef = useRef();

  const handleFiles = (files) => {
    const file = files[0];
    if (!file) return;
    const existing = documents.filter(d => d.document_type === 'vendor_invoice');
    existing.forEach(d => onRemove(d._key || d.id));
    onUpload(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const vendorInvoiceDocs = documents.filter(d => d.document_type === 'vendor_invoice');

  return (
    <div>
      <label style={label}>Upload Vendor Invoice</label>
      {vendorInvoiceDocs.length === 0 && (
        <div
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          style={{
            border: `2px dashed ${dragging ? '#6366f1' : '#d1d5db'}`,
            borderRadius: 10,
            padding: '20px 16px',
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            background: dragging ? '#eef2ff' : '#f9fafb',
            transition: 'all 0.15s',
          }}
        >
          <FiUploadCloud size={22} style={{ color: dragging ? '#6366f1' : '#9ca3af', marginBottom: 6 }} />
          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
            {uploading ? 'Uploading…' : <><span style={{ fontWeight: 600, color: '#6366f1' }}>Click to upload</span> or drag & drop</>}
          </div>
          <div style={{ fontSize: '0.76rem', color: '#9ca3af', marginTop: 3 }}>PDF, PNG, JPG up to any size</div>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" style={{ display: 'none' }} disabled={uploading} onChange={e => handleFiles(e.target.files)} />
        </div>
      )}

      {vendorInvoiceDocs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
          {vendorInvoiceDocs.map(d => (
            <div key={d._key || d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13 }}>
              <FiUploadCloud size={13} style={{ color: '#16a34a', flexShrink: 0 }} />
              <span style={{ flex: 1, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.document_name}
              </span>
              <button
                onClick={() => setPreviewDoc(d)}
                title="Preview invoice"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
              >
                <FiEye size={14} color="#6366f1" />
              </button>
              <button
                onClick={() => onRemove(d._key || d.id)}
                title="Remove"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
              >
                <FiX size={13} color="#dc2626" />
              </button>
            </div>
          ))}
        </div>
      )}

      {previewDoc && (
        <InvoicePreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
      )}
    </div>
  );
}

/* ── Items Table ────────────────────────────────────────────────── */
function ItemsTable({ items, setItems }) {
  const rowRefs  = useRef({});
  const nameRefs = useRef({});

  const update = (key, field, value) =>
    setItems(prev => prev.map(it => it._key === key ? { ...it, [field]: value } : it));

  const remove = (key) =>
    setItems(prev => prev.filter(it => it._key !== key));

  const addItem = () => {
    const n = blankItem();
    setItems(prev => [...prev, n]);
    return n._key;
  };

  const isEmpty = (it) =>
    !it.item_name.trim() && !it.description.trim() &&
    !Number(it.quantity) && !Number(it.rate);

  const handleGstKeyDown = (key, idx, e) => {
    if (e.key === 'Tab' && !e.shiftKey && idx === items.length - 1) {
      e.preventDefault();
      const cur = items[idx];
      if (isEmpty(cur)) {
        if (items.length > 1) setItems(prev => prev.filter(it => it._key !== cur._key));
        setTimeout(() => { document.querySelector('.purchase-notes-textarea')?.focus(); }, 0);
      } else {
        const newKey = addItem();
        setTimeout(() => { nameRefs.current[newKey]?.focus(); }, 0);
      }
    }
  };

  const handleRowBlur = (key, e) => {
    const el = rowRefs.current[key];
    if (el && el.contains(e.relatedTarget)) return;
    setItems(prev => {
      const it = prev.find(x => x._key === key);
      if (it && isEmpty(it) && prev.length > 1) return prev.filter(x => x._key !== key);
      return prev;
    });
  };

  const lineTotal = (it) => {
    const taxable  = Math.max(0, Number(it.quantity) * Number(it.rate) - Number(it.discount_amount || 0));
    const half     = (Number(it.tax_rate) || 0) / 2;
    const cgst     = Number((taxable * half / 100).toFixed(2));
    const sgst     = Number((taxable * half / 100).toFixed(2));
    return Number((taxable + cgst + sgst).toFixed(2));
  };

  const cellStyle = { padding: '8px 6px', verticalAlign: 'top' };
  const thStyle   = { padding: '8px 6px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', borderBottom: '2px solid #f3f4f6' };

  return (
    <div>
      {/* ── Desktop / tablet table ── */}
      <div className="apf-items-wrapper apf-items-table-desktop">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#fafafa' }}>
              <th style={{ ...thStyle, width: '30%' }}>Item / Work *</th>
              <th style={{ ...thStyle, width: 70 }}>Qty *</th>
              <th style={{ ...thStyle, width: 90 }}>Unit</th>
              <th style={{ ...thStyle, width: 100 }}>Rate *</th>
              <th style={{ ...thStyle, width: 80 }}>GST %</th>
              <th style={{ ...thStyle, width: 110, textAlign: 'right' }}>Total</th>
              <th style={{ ...thStyle, width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <tr
                key={it._key}
                ref={el => { rowRefs.current[it._key] = el; }}
                onBlur={e => handleRowBlur(it._key, e)}
                style={{ borderBottom: '1px solid #f3f4f6' }}
              >
                <td style={cellStyle}>
                  <input
                    ref={el => { nameRefs.current[it._key] = el; }}
                    style={{ ...input, fontWeight: 600, marginBottom: 4 }}
                    placeholder="Item / work name"
                    value={it.item_name}
                    onChange={e => update(it._key, 'item_name', e.target.value)}
                  />
                  <input
                    style={{ ...input, fontSize: 12, color: '#6b7280' }}
                    placeholder="Description (optional)"
                    value={it.description || ''}
                    onChange={e => update(it._key, 'description', e.target.value)}
                  />
                </td>
                <td style={cellStyle}>
                  <input type="number" min="0" style={input} value={it.quantity} onChange={e => update(it._key, 'quantity', e.target.value)} placeholder="0" />
                </td>
                <td style={cellStyle}>
                  <select style={select} value={it.unit} onChange={e => update(it._key, 'unit', e.target.value)}>
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </td>
                <td style={cellStyle}>
                  <input type="number" min="0" step="0.01" style={input} value={it.rate} onChange={e => update(it._key, 'rate', e.target.value)} placeholder="0.00" />
                </td>
                <td style={cellStyle}>
                  <input
                    type="number" min="0" max="100" step="0.01"
                    style={input}
                    value={it.tax_rate}
                    onChange={e => update(it._key, 'tax_rate', Number(e.target.value))}
                    onKeyDown={e => handleGstKeyDown(it._key, idx, e)}
                    placeholder="0"
                  />
                </td>
                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 700 }}>₹{money(lineTotal(it))}</td>
                <td style={cellStyle}>
                  <button
                    onClick={() => remove(it._key)}
                    disabled={items.length === 1}
                    style={{ background: items.length === 1 ? '#f3f4f6' : '#fee2e2', border: 'none', borderRadius: 6, padding: '5px 7px', cursor: items.length === 1 ? 'not-allowed' : 'pointer' }}
                  >
                    <FiTrash2 size={13} color={items.length === 1 ? '#9ca3af' : '#dc2626'} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Mobile card layout ── */}
      <div>
        {items.map((it, idx) => (
          <div key={it._key} className="apf-item-card">
            <div className="apf-item-card__header">
              <span className="apf-item-card__badge">Item {idx + 1}</span>
              <button
                onClick={() => remove(it._key)}
                disabled={items.length === 1}
                style={{ background: items.length === 1 ? '#f3f4f6' : '#fee2e2', border: 'none', borderRadius: 6, padding: '5px 7px', cursor: items.length === 1 ? 'not-allowed' : 'pointer' }}
              >
                <FiTrash2 size={13} color={items.length === 1 ? '#9ca3af' : '#dc2626'} />
              </button>
            </div>
            <div className="apf-item-card__grid">
              <div className="apf-item-card__full">
                <label style={label}>Item / Work *</label>
                <input
                  ref={el => { nameRefs.current[it._key] = el; }}
                  style={{ ...input, fontWeight: 600, marginBottom: 4 }}
                  placeholder="Item / work name"
                  value={it.item_name}
                  onChange={e => update(it._key, 'item_name', e.target.value)}
                />
                <input
                  style={{ ...input, fontSize: 12, color: '#6b7280' }}
                  placeholder="Description (optional)"
                  value={it.description || ''}
                  onChange={e => update(it._key, 'description', e.target.value)}
                />
              </div>
              <div>
                <label style={label}>Qty *</label>
                <input type="number" min="0" style={input} value={it.quantity} onChange={e => update(it._key, 'quantity', e.target.value)} placeholder="0" />
              </div>
              <div>
                <label style={label}>Unit</label>
                <select style={select} value={it.unit} onChange={e => update(it._key, 'unit', e.target.value)}>
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label style={label}>Rate *</label>
                <input type="number" min="0" step="0.01" style={input} value={it.rate} onChange={e => update(it._key, 'rate', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label style={label}>GST %</label>
                <input
                  type="number" min="0" max="100" step="0.01"
                  style={input}
                  value={it.tax_rate}
                  onChange={e => update(it._key, 'tax_rate', Number(e.target.value))}
                  onKeyDown={e => handleGstKeyDown(it._key, idx, e)}
                  placeholder="0"
                />
              </div>
              <div className="apf-item-card__total">
                Total: ₹{money(lineTotal(it))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={() => { const k = addItem(); setTimeout(() => nameRefs.current[k]?.focus(), 0); }}
        className="apf-items-add-btn"
        style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: '4px 0' }}
      >
        <FiPlus size={13} /> Add Item
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════ */
export default function AdminPurchaseForm() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = Boolean(id);
  const { log }  = useStaffLog();

  const [loading,    setLoading]    = useState(isEdit);
  const [saving,     setSaving]     = useState(false);
  const [uploading,  setUploading]  = useState(false);

  const [vendor,             setVendor]             = useState(null);
  const vendorId = vendor?.id || '';
  const [selectedOrder,      setSelectedOrder]       = useState(null);
  const [purchaseType,       setPurchaseType]        = useState('MATERIAL');
  const [vendorInvoiceNum,   setVendorInvoiceNum]    = useState('');
  const [vendorInvoiceDate,  setVendorInvoiceDate]   = useState('');
  const [purchaseDate,       setPurchaseDate]        = useState(new Date().toISOString().split('T')[0]);
  const [dueDate,            setDueDate]             = useState('');
  const [paymentTermsDays,   setPaymentTermsDays]    = useState(0);
  const taxType = 'INTRA_STATE';
  const [otherCharges,       setOtherCharges]        = useState(0);
  // null = automatic round-off; string = value typed by the user (manual override)
  const [roundOffManual,     setRoundOffManual]      = useState(null);
  const [notes,              setNotes]               = useState('');
  const [items,              setItems]               = useState([blankItem()]);
  const [documents,          setDocuments]           = useState([]);
  const [purchaseNumber,     setPurchaseNumber]      = useState('');

  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      try {
        const { data } = await API.get(`/purchases/${id}`);
        const p = data.purchase;
        setPurchaseNumber(p.purchase_number);
        if (p.vendor) {
          const v = p.vendor;
          const address = [v.address_line1, v.address_line2, v.city, v.state, v.pincode].filter(Boolean).join(', ');
          setVendor({
            id:             v.id,
            vendor_code:    v.vendor_code,
            company_name:   v.company_name,
            contact_person: v.contact_person || '',
            phone:          v.phone || '',
            email:          v.email || '',
            gstin:          v.gstin || '',
            address,
            payment_terms_days: v.payment_terms_days || 0,
          });
        }
        if (p.order) setSelectedOrder(p.order);
        setPurchaseType(p.purchase_type);
        setVendorInvoiceNum(p.vendor_invoice_number || '');
        setVendorInvoiceDate(p.vendor_invoice_date || '');
        setPurchaseDate(p.purchase_date);
        setDueDate(p.due_date || '');
        setPaymentTermsDays(p.payment_terms_days || 0);
        setOtherCharges(p.other_charges || 0);
        // Saved round-off differs from what auto would give -> it was set manually
        const loadedAuto = computePurchaseTotals(p.items || [], p.other_charges || 0, p.tax_type || 'INTRA_STATE').autoRoundOff;
        const savedRoff  = Number(p.round_off) || 0;
        setRoundOffManual(Math.abs(savedRoff - loadedAuto) > 0.004 ? String(savedRoff) : null);
        setNotes(p.notes || '');
        setItems((p.items || []).map(i => ({ ...i, _key: i.id })));
        setDocuments(p.documents || []);
      } catch {
        toast.error('Failed to load purchase');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEdit]);

  const handleVendorSelect = (v) => {
    setVendor(v);
    if (v) {
      const days = v.payment_terms_days || 0;
      setPaymentTermsDays(days);
      if (days > 0 && purchaseDate) {
        const d = new Date(purchaseDate);
        d.setDate(d.getDate() + days);
        setDueDate(d.toISOString().split('T')[0]);
      }
    }
  };

  const handlePurchaseDateChange = (val) => {
    setPurchaseDate(val);
    if (paymentTermsDays > 0 && val) {
      const d = new Date(val);
      d.setDate(d.getDate() + Number(paymentTermsDays));
      setDueDate(d.toISOString().split('T')[0]);
    }
  };

  const handlePaymentTermsChange = (val) => {
    setPaymentTermsDays(val);
    if (Number(val) > 0 && purchaseDate) {
      const d = new Date(purchaseDate);
      d.setDate(d.getDate() + Number(val));
      setDueDate(d.toISOString().split('T')[0]);
    }
  };

  const totals = computePurchaseTotals(items, otherCharges, taxType, roundOffManual);

  const handleDocUpload = async (file, docType = 'vendor_invoice') => {
    if (!file) return;

    let cachedPdf = { gstin: null, invoiceNumber: null, invoiceDate: null };
    if (docType === 'vendor_invoice' && file.name.toLowerCase().endsWith('.pdf')) {
      cachedPdf = await parsePdfInvoice(file);
    }

    if (docType === 'vendor_invoice' && file.name.toLowerCase().endsWith('.pdf')) {
      const { invoiceNumber: preCheckNum, gstin: preCheckGstin } = cachedPdf;
      if (preCheckNum) {
        try {
          let checkVendorId = vendor?.id || null;
          if (!checkVendorId && preCheckGstin) {
            const { data: vData } = await API.get('/vendors', { params: { q: preCheckGstin, limit: 5, status: 'active' } });
            const matched = (vData.vendors || []).find(v =>
              v.gstin?.trim().toUpperCase() === preCheckGstin.toUpperCase()
            );
            if (matched) checkVendorId = matched.id;
          }
          if (checkVendorId) {
            const { data: dupCheck } = await API.get('/purchases', {
              params: { vendor_invoice_number: preCheckNum, vendor_id: checkVendorId, limit: 1 },
            });
            if ((dupCheck.purchases || []).length > 0) {
              toast.error(`Invoice ${preCheckNum} already exists for this vendor`);
              return;
            }
          }
        } catch { /* silent */ }
      }
    }

    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await API.post('/upload/purchase-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocuments(prev => [...prev, {
        _key:          Date.now(),
        document_type: docType,
        document_name: file.name,
        file_path:     data.url,
        isNew:         true,
      }]);

      if (docType === 'vendor_invoice' && file.name.toLowerCase().endsWith('.pdf')) {
        const { gstin, invoiceNumber, invoiceDate } = cachedPdf;
        let filled = [];
        if (invoiceNumber) { setVendorInvoiceNum(invoiceNumber); filled.push('Invoice No'); }
        if (invoiceDate)   { setVendorInvoiceDate(invoiceDate);  filled.push('Invoice Date'); }
        if (gstin && (!vendor || vendor.gstin?.trim().toUpperCase() !== gstin.toUpperCase())) {
          try {
            const { data: vData } = await API.get('/vendors', { params: { q: gstin, limit: 5, status: 'active' } });
            const matched = (vData.vendors || []).find(v =>
              v.gstin?.trim().toUpperCase() === gstin.toUpperCase()
            );
            if (matched) {
              const address = [matched.address_line1, matched.address_line2, matched.city, matched.state, matched.pincode]
                .filter(Boolean).join(', ');
              handleVendorSelect({
                id:                 matched.id,
                vendor_code:        matched.vendor_code,
                company_name:       matched.company_name,
                contact_person:     matched.contact_person || '',
                phone:              matched.phone || '',
                email:              matched.email || '',
                gstin:              matched.gstin || '',
                address,
                payment_terms_days: matched.payment_terms_days || 0,
                vendor_type_id:     matched.vendor_type_id || null,
                vendorType:         matched.vendorType || null,
              });
              filled.push('Vendor');
            } else if (gstin) {
              toast(`GSTIN ${gstin} not found in vendor database`, { icon: '⚠️' });
            }
          } catch { /* silent */ }
        }
        if (filled.length > 0) {
          toast.success(`Auto-filled: ${filled.join(', ')}`);
        } else {
          toast('Could not extract fields from this PDF', { icon: '⚠️' });
        }
      } else {
        toast.success('File uploaded');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = (key) => setDocuments(prev => prev.filter(d => (d._key || d.id) !== key));

  const handleSubmit = async () => {
    if (!vendor?.id)     return toast.error('Select a vendor');
    if (!purchaseDate)   return toast.error('Purchase date is required');
    if (items.some(i => !i.item_name?.trim())) return toast.error('All items need a name');
    if (items.some(i => !Number(i.quantity)))  return toast.error('All items need a quantity');
    if (items.some(i => !Number(i.rate)))      return toast.error('All items need a rate');
    if (totals.grandTotal < 0)                 return toast.error('Grand total cannot be negative — check Other Charges / Round Off');

    const payload = {
      vendor_id:             Number(vendor.id),
      order_id:              selectedOrder?.id || null,
      purchase_type:         purchaseType,
      vendor_invoice_number: vendorInvoiceNum   || null,
      vendor_invoice_date:   vendorInvoiceDate  || null,
      purchase_date:         purchaseDate,
      due_date:              dueDate            || null,
      payment_terms_days:    Number(paymentTermsDays) || 0,
      tax_type:              taxType,
      other_charges:         Number(otherCharges) || 0,
      round_off:             Number(totals.roundOff.toFixed(2)),
      notes:                 notes              || null,
      items: items.map(i => ({
        item_name:       i.item_name,
        description:     i.description || null,
        quantity:        Number(i.quantity),
        unit:            i.unit,
        rate:            Number(i.rate),
        discount_amount: Number(i.discount_amount) || 0,
        tax_rate:        Number(i.tax_rate) || 0,
      })),
    };

    try {
      setSaving(true);
      let savedPurchase;
      if (isEdit) {
        const { data } = await API.put(`/purchases/${id}`, payload);
        savedPurchase = data.purchase;
      } else {
        const { data } = await API.post('/purchases', payload);
        savedPurchase = data.purchase;
      }

      const newDocs = documents.filter(d => d.isNew);
      await Promise.all(newDocs.map(d =>
        API.post(`/purchases/${savedPurchase.id}/documents`, {
          document_type: d.document_type,
          document_name: d.document_name,
          file_path:     d.file_path,
        })
      ));

      toast.success(isEdit ? 'Purchase updated' : 'Purchase created');
      await log(
        isEdit ? 'updated_purchase' : 'created_purchase',
        `${isEdit ? 'Updated' : 'Created'} purchase #${savedPurchase.purchase_number}`,
        'Purchase', savedPurchase.id
      );
      navigate(`/admin/purchases/${savedPurchase.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>;

  return (
    <div className="apf-page">

      {/* ── Header ── */}
      <div className="apf-header">
        <button onClick={() => navigate('/admin/purchases')} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '7px 10px', cursor: 'pointer' }}>
          <FiArrowLeft size={16} />
        </button>
        <h2>{isEdit ? 'Edit Purchase' : 'New Purchase'}</h2>
        {purchaseNumber && (
          <span className="apf-header-badge">{purchaseNumber}</span>
        )}
      </div>

      {/* ── Main card ── */}
      <div className="crm-card apf-main-card">

        {/* Top grid: Vendor | Purchase details */}
        <div className="apf-top-grid">

          {/* LEFT: Vendor */}
          <VendorBlock vendor={vendor} setVendor={handleVendorSelect} />

          {/* RIGHT: Purchase detail fields */}
          <div className="apf-details-grid">
            <div>
              <label style={label}>Purchase Date *</label>
              <input type="date" style={{ ...input, cursor: 'pointer' }} value={purchaseDate} onChange={e => handlePurchaseDateChange(e.target.value)} onClick={e => e.target.showPicker?.()} />
            </div>
            <div>
              <label style={label}>Vendor Invoice Number</label>
              <input style={input} value={vendorInvoiceNum} onChange={e => setVendorInvoiceNum(e.target.value)} placeholder="Supplier's invoice no." />
            </div>
            <div>
              <label style={label}>Vendor Invoice Date</label>
              <input type="date" style={{ ...input, cursor: 'pointer' }} value={vendorInvoiceDate} onChange={e => setVendorInvoiceDate(e.target.value)} onClick={e => e.target.showPicker?.()} />
            </div>
            <div>
              <label style={label}>Linked Customer Order (optional)</label>
              <OrderSearch value={selectedOrder} onChange={setSelectedOrder} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <DragDropUpload
                documents={documents}
                onUpload={(file) => handleDocUpload(file, 'vendor_invoice')}
                onRemove={removeDoc}
                uploading={uploading}
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="apf-section-head">Items</div>
        <ItemsTable items={items} setItems={setItems} />

        {/* Bottom: notes | totals */}
        <div className="apf-bottom-grid">
          <div>
            <label style={label}>Notes</label>
            <textarea
              className="purchase-notes-textarea"
              style={{ ...input, minHeight: 140, resize: 'vertical' }}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Additional information about this purchase…"
            />
          </div>

          <div className="apf-totals-panel">
            {[
              { label: 'Subtotal',       value: totals.subtotal },
              { label: 'Taxable Amount', value: totals.taxableTotal, bold: true },
              { label: 'CGST',           value: totals.cgstTotal },
              { label: 'SGST',           value: totals.sgstTotal },
            ].map(r => (
              <div key={r.label} className="apf-totals-row">
                <span style={{ color: '#6b7280' }}>{r.label}</span>
                <span style={{ fontWeight: r.bold ? 700 : 500, color: '#374151' }}>₹{money(r.value)}</span>
              </div>
            ))}
            <div className="apf-totals-row--charges">
              <span style={{ color: '#6b7280' }}>Other Charges (₹)</span>
              <input
                type="number" step="0.01"
                style={{ ...input, width: 130, textAlign: 'right' }}
                value={otherCharges}
                onChange={e => setOtherCharges(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="apf-totals-row--charges">
              <span style={{ color: '#6b7280' }}>
                Round Off (₹){' '}
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 10, verticalAlign: 'middle',
                  background: totals.isManual ? '#fef3c7' : '#e0e7ff',
                  color:      totals.isManual ? '#92400e' : '#3730a3',
                }}>
                  {totals.isManual ? 'Edited' : 'Auto'}
                </span>
              </span>
              <input
                type="text"
                inputMode="decimal"
                style={{ ...input, width: 130, textAlign: 'right', color: totals.roundOff < 0 ? '#dc2626' : undefined }}
                value={totals.isManual ? roundOffManual : totals.autoRoundOff.toFixed(2)}
                onChange={e => {
                  const v = e.target.value.trim();
                  // allow: empty, "-", "+", "-0.", "+0.25", "12.5" (max 2 decimals)
                  if (/^[+-]?\d*\.?\d{0,2}$/.test(v)) setRoundOffManual(v);
                }}
                onFocus={e => e.target.select()}
                placeholder="0.00"
              />
            </div>
            {totals.isManual && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: -4, marginBottom: 8, fontSize: 12 }}>
                <span style={{ color: '#9ca3af' }}>Auto would be {totals.autoRoundOff >= 0 ? '+' : ''}₹{money(totals.autoRoundOff)}</span>
                <button
                  type="button"
                  onClick={() => setRoundOffManual(null)}
                  style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}
                >
                  Reset to auto
                </button>
              </div>
            )}
            {Math.abs(totals.roundOff) > ROUND_OFF_WARN && (
              <div style={{ marginBottom: 8, fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 8px' }}>
                ⚠ Round off is more than ₹{ROUND_OFF_WARN} — please double-check.
              </div>
            )}
            <div className="apf-grand-total">
              <span>Grand Total</span>
              <span style={{ color: '#6366f1' }}>₹{money(totals.grandTotal)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Actions ── */}
      <div className="apf-actions">
        <button className="crm-btn" onClick={() => navigate('/admin/purchases')}>Cancel</button>
        <button className="crm-btn crm-btn--primary" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Purchase'}
        </button>
      </div>
    </div>
  );
}